import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { isSafari } from 'react-device-detect';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { Loading } from '../components/Loading';
import * as routes from '../constants/Routes';
import { firebase } from '../firebase';
import { api } from '../server/api';
import { getUrlClient, resolveClientAlias } from '../utils/getUrlClient';
import { Notify } from '../components/Notify';
import { decodeSafedata } from '../utils/decodeSafeData';
import { safeData } from '../utils/safeData';
import { resolveFavicon } from '../utils/hostBranding';

interface AuthContextProps {
  signed: boolean | null;
  playerAuth: any | undefined | null;
  client: any | undefined | null;
  player: any | undefined | null;
  notifications: any | undefined | null;
  loadingAuth: any | undefined | null | boolean;
  loadingClient: boolean;
  notificationCategory2: any | undefined | null;
  notificationCategory1: any | undefined | null;
  notificationCount: any | undefined | null;
  isEmptyCorretor: any | undefined | null | boolean;
  isBrokerDomain: boolean;
  getClientByAliasAndPlayer: () => Promise<any | undefined>;
  getNotifications: () => Promise<any | undefined>;
  getClientByUrl: () => Promise<any | undefined>;
  getClientByClientIdAndPlayerId: (
    clientId: string,
    playerId: string
  ) => Promise<any | undefined>;
  signIn(credentials: SignInProps): Promise<any>;
  signOut: () => void;
  setClient: Dispatch<SetStateAction<any | undefined>>;
  setNotificationCount: Dispatch<SetStateAction<any | undefined>>;
  setIsEmptyCorretor: Dispatch<
    SetStateAction<any | undefined | null | boolean>
  >;
  setPlayerAuth: Dispatch<SetStateAction<any | undefined>>;
  setPlayer: Dispatch<SetStateAction<any | undefined>>;
}
interface SignInProps {
  email: string;
  password: string;
  clientId: string;
}

export const authContext = createContext({} as AuthContextProps);
interface AuthContextProviderProps {
  children: ReactNode;
}
export function AuthContextProvider(props: AuthContextProviderProps) {
  const [playerAuth, setPlayerAuth] = useState<any | null>();
  const [player, setPlayer] = useState<any | null>();
  const [notifications, setNotifications] = useState<any | null>();
  const [notificationCategory2, setNotificationCategory2] = useState<
    any | null
  >();
  const [notificationCategory1, setNotificationCategory1] = useState<
    any | null
  >();
  const [client, setClient] = useState<any | null>();
  const [loadingClient, setLoadingClient] = useState<boolean>(false);
  const [loadingAuth, setLoadingAuth] = useState<boolean>(true);
  const [notificationCount, setNotificationCount] = useState<any>();
  const [isEmptyCorretor, setIsEmptyCorretor] = useState<boolean>(false);
  const [isBrokerDomain, setIsBrokerDomain] = useState<boolean>(false);
  const location = useLocation();
  useEffect(() => {
    const request = async () => {
      // Verifica se veio de registro via iframe
      const urlParams = new URLSearchParams(window.location.search);
      const fromIframeRegister = urlParams.get('fromIframeRegister') === 'true';
      const iframeAccessToken = urlParams.get('iat');
      const iframeRefreshToken = urlParams.get('irt');
      const iframeClientId = urlParams.get('iclient');
      const iframeHostPlayerId = urlParams.get('ihost');

      // Cadastro feito dentro do iframe (site do cliente): o localStorage
      // gravado lá dentro fica em outro "compartimento" de storage (Safari
      // sempre particiona storage de terceiros, Chrome/Firefox também vêm
      // adotando isso), então essa aba nova não enxerga o que o signIn()
      // gravou. Re-hidrata a sessão a partir do token vindo pela URL.
      if (fromIframeRegister && iframeAccessToken && iframeClientId) {
        try {
          api.defaults.headers.common['Authorization'] =
            `OQVCQUER ${iframeAccessToken}`;
          api.defaults.headers.common['refresh'] = iframeRefreshToken || '';

          const authUser = {
            stsTokenManager: {
              accessToken: iframeAccessToken,
              refreshToken: iframeRefreshToken || '',
            },
          };
          setPlayerAuth(authUser);
          localStorage.setItem(
            '@pwa:playerAuth',
            safeData(JSON.stringify(authUser))
          );

          const responseClientPublic = await api.post('/clientPublicData', {
            searchFunctionality: 'getClientPublicData',
            userType: 'lead',
            clientId: iframeClientId,
            playerId: iframeHostPlayerId || '',
          });
          const resolvedClient = responseClientPublic.data.data.clientData;
          setClient(resolvedClient);
          localStorage.setItem(
            '@pwa:client',
            safeData(JSON.stringify(resolvedClient))
          );

          await getPlayer(resolvedClient.id);
        } catch (error) {
          console.log('iframe session restore error: ', error);
          setLoadingAuth(false);
        }

        urlParams.delete('fromIframeRegister');
        urlParams.delete('iat');
        urlParams.delete('irt');
        urlParams.delete('iclient');
        urlParams.delete('ihost');
        const newUrlAfterRestore = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
        window.history.replaceState({}, '', newUrlAfterRestore);
        return;
      }

      // Se veio do iframe (sem token na URL — fallback legado), aguarda um
      // momento para sincronização do localStorage
      if (fromIframeRegister) {
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Remove o parâmetro da URL
        urlParams.delete('fromIframeRegister');
        const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
        window.history.replaceState({}, '', newUrl);
      }

      let playerAuthLocal = localStorage.getItem('@pwa:playerAuth');
      let playerLocal = localStorage.getItem('@pwa:player');
      let clientLocal = localStorage.getItem('@pwa:client');
      const isAction = location.pathname.match(/\/action\//) ? true : false;
      const isLogin = location.pathname.match(/\/_\//) ? true : false;

      if (
        playerAuthLocal &&
        playerLocal &&
        clientLocal &&
        !isAction &&
        !isLogin
      ) {
        playerAuthLocal = decodeSafedata(playerAuthLocal);
        playerLocal = decodeSafedata(playerLocal);
        clientLocal = decodeSafedata(clientLocal);
        const playerAuthparse = playerAuthLocal
          ? JSON.parse(JSON.parse(playerAuthLocal))
          : null;
        const playerparse = playerLocal
          ? JSON.parse(JSON.parse(playerLocal))
          : null;
        const clientparse = clientLocal
          ? JSON.parse(JSON.parse(clientLocal))
          : null;

        const accessToken = playerAuthparse.stsTokenManager.accessToken;
        const refreshToken = playerAuthparse.stsTokenManager.refreshToken;
        api.defaults.headers.common['Authorization'] =
          `OQVCQUER ${accessToken}`;
        api.defaults.headers.common['refresh'] = refreshToken;
        setPlayerAuth(playerAuthparse);
        setPlayer(playerparse.player);
        setClient(clientparse);
        await getNotifications();
        setLoadingAuth(false);
      } else {
        if (isAction && playerAuthLocal) {
          try {
            const decoded = decodeSafedata(playerAuthLocal);
            const parsed = JSON.parse(JSON.parse(decoded));
            api.defaults.headers.common['Authorization'] =
              `OQVCQUER ${parsed.stsTokenManager.accessToken}`;
            api.defaults.headers.common['refresh'] =
              parsed.stsTokenManager.refreshToken;
          } catch (error) {
            console.log('Error decoding playerAuthLocal: ', error);
          }
        }
        localStorage.removeItem('@pwa:playerAuth');
        localStorage.removeItem('@pwa:client');
        localStorage.removeItem('@pwa:player');
        await getClientByUrl();
        setPlayerAuth(null);
        setPlayer(null);
      }
    };
    request();
  }, []);

  const updateLastLogin = async (playerId: string) => {
    try {
      const parameters = {
        updateFunctionality: 'updateLastLogin',
        userType: 'player',
        clientId: client.id,
        playerId,
      };
      await api.post(`/update`, parameters);
    } catch (error) {
      console.log('updateLastLogin error: ', error);
    }
  };

  async function getClientByAliasAndPlayer(clientUrl?: string) {
    try {
      // setLoadingAuth(true);
      const knownAlias = clientUrl || client.clientUrl;
      const resolvedAlias = knownAlias
        ? { clientAlias: knownAlias, isUrl: false }
        : resolveClientAlias();
      const parameters = {
        searchFunctionality: 'getClientByAlias',
        userType: 'player',
        clientAlias: resolvedAlias.clientAlias,
        isUrl: resolvedAlias.isUrl,
      };
      const responseClientAlias = await api.post(`/search`, parameters);
      if (responseClientAlias.data.success) {
        setClient(responseClientAlias.data.data.client);
        await getPlayer(responseClientAlias.data.data.client.id);
      } else {
        setLoadingAuth(false);
        // navigate(routes.START);
        window.location.href = routes.HOME;
      }
    } catch (error) {
      console.log('getClientByAliasAndPlayer error: ', error);
    }
  }

  async function getClientByClientIdAndPlayerId(
    clientId: string,
    playerId: string
  ) {
    const parameters = {
      searchFunctionality: 'getClientPublicData',
      userType: 'lead',
      clientId,
      playerId,
    };
    try {
      const { data } = await api.post(`/clientPublicData`, parameters);
      setClient(data.data.clientData);
      getClientByAliasAndPlayer(data.data.clientData.clientUrl);
      setLoadingClient(false);
    } catch (error) {
      console.log('getClientByClientIdAndPlayerId', error);
    }
  }

  async function getClientByUrl() {
    try {
      setLoadingClient(true);
      const methodGet: any = getUrlClient();
      if (methodGet?.type === 'clientAliasBroker') {
        setIsBrokerDomain(true);
      }
      const knownAlias = methodGet?.link || client?.clientUrl;
      const resolvedAlias = knownAlias
        ? { clientAlias: knownAlias, isUrl: false }
        : resolveClientAlias(methodGet);
      const parameters = {
        searchFunctionality: 'getClientByAlias',
        userType: 'player',
        clientAlias: resolvedAlias.clientAlias,
        isUrl: resolvedAlias.isUrl,
      };
      const responseClientAlias = await api.post(`/search`, parameters);
      // Custom broker domains are resolved by the backend (client.brokerDomain),
      // so they don't need to be listed in HOST_ALIAS_BROKER_MAP.
      if (responseClientAlias.data?.data?.isBrokerDomain) {
        setIsBrokerDomain(true);
      }
      setClient(responseClientAlias.data?.data?.client);
      setLoadingClient(false);
      setLoadingAuth(false);
    } catch (error) {
      console.log('getClientByUrl error: ', error);
      setLoadingClient(false);
      setLoadingAuth(false);
    }
  }

  async function signIn({ email, password, clientId }: SignInProps) {
    const parameters = {
      searchFunctionality: 'getPlayerAuth',
      userType: 'player',
      email,
      password,
      clientId,
    };
    const responseAuth = await api.post('/search', parameters);
    if (!responseAuth.data.success) {
      throw Error(responseAuth.data.data);
    }
    const { user, playerSnap, clientSnap } = responseAuth.data.data;
    const accessToken = user.stsTokenManager.accessToken;
    const refreshToken = user.stsTokenManager.refreshToken;
    api.defaults.headers.common['Authorization'] = `OQVCQUER ${accessToken}`;
    api.defaults.headers.common['refresh'] = refreshToken;
    await getPlayer(clientId);
    if (playerSnap.id) {
      sessionStorage.removeItem('clientId');
      sessionStorage.removeItem('playerId');
      sessionStorage.removeItem('action');
    }
    if (
      clientSnap.redirectWhatsapp &&
      clientSnap.redirectWhatsapp === 'true' &&
      playerSnap.phoneWhatsapp &&
      playerSnap.phoneWhatsapp.trim() !== ''
    ) {
      localStorage.setItem('phoneWhatsapp', 'true');
    } else {
      localStorage.removeItem('phoneWhatsapp');
    }
    setPlayerAuth(user);
    localStorage.setItem('@pwa:playerAuth', safeData(JSON.stringify(user)));
    localStorage.setItem('@pwa:client', safeData(JSON.stringify(client)));
    // adicionar lastLogin
    await updateLastLogin(playerSnap.id);
    Notify(`Olá, ${user.displayName}`, 'success');
  }

  function signOut() {
    setPlayerAuth(null);
    setPlayer(null);
    setClient(null);
    localStorage.removeItem('@pwa:playerAuth');
    localStorage.removeItem('@pwa:client');
    localStorage.removeItem('@pwa:player');
    sessionStorage.removeItem('cart');
    sessionStorage.removeItem('remainingPoints');
  }
  async function getNotifications() {
    let clientLocal = localStorage.getItem('@pwa:client');
    if (clientLocal) {
      clientLocal = decodeSafedata(clientLocal);
    }
    const clientparse = clientLocal
      ? JSON.parse(JSON.parse(clientLocal))
      : null;
    const parameters = {
      searchFunctionality: 'getNotifications',
      userType: 'player',
      typeNotification: 'bell',
      clientId: clientparse?.id,
    };

    try {
      const res = await api.post('/search', parameters);

      const notifificationCategory: any = [];
      const notifificationCategory1: any = [];
      let countNotification = 0;

      res.data.data.notifications.forEach((notification: any) => {
        if (notification.notificationType === 'newLead') {
          notifificationCategory.push(notification);
        } else {
          notifificationCategory1.push(notification);
        }
        if (notification.playersWhoRead) {
          const playerRead = Object.values(
            notification.playersWhoRead
          ).includes(player && player.id);
          if (!playerRead) {
            countNotification = countNotification - 1;
          }
        } else {
          countNotification = countNotification + 1;
        }
        if (notification.scope === 'direct' && !notification.sended) {
          countNotification = countNotification + 1;
        }
      });
      if (countNotification > 9) {
        setNotificationCount('9');
      } else if (countNotification > 0 && countNotification <= 9) {
        setNotificationCount(String(countNotification));
      }
      setNotificationCategory2(notifificationCategory);
      setNotificationCategory1(notifificationCategory1);
      setNotifications(res.data.data.notifications);
    } catch (error) {
      console.log('getNotifications error: ', error);
    }
  }

  async function getPlayer(clientId: string) {
    try {
      const parameters = {
        searchFunctionality: 'getSelfPlayer',
        userType: 'player',
        clientId: clientId,
      };
      const res = await api.post(`/search`, parameters);
      const result = res.data.data;

      delete result.client;
      delete result.listPlayersHost;
      delete result.playerRanking;
      delete result.pointsLeads;
      delete result.pontuationRules;
      delete result.rules;
      delete result.boost;

      const acceptedTerms = result.player && !!result.player.acceptedTerms;
      localStorage.setItem('acceptedTerms', String(acceptedTerms));
      localStorage.setItem('@pwa:player', safeData(JSON.stringify(result)));
      setPlayer(result.player);
      setLoadingAuth(false);
      getPermissionNotification(result.player.id, result.player.clientId);
    } catch (error) {
      console.log('getPlayer error: ', error);
      setLoadingAuth(false);
    }
  }

  async function getPermissionNotification(playerId: string, clientId: string) {
    try {
      if (!isSafari) {
        if (Notification.permission !== 'denied') {
          await Notification.requestPermission();
          const token = await firebase.messaging.getToken();
          if (token) {
            await updatePlayerToken(token, playerId, clientId);
          }
        }
      }
    } catch (error) {
      console.log('getPermissionNotification error: ', error);
    }
  }

  async function updatePlayerToken(
    fcmToken: string,
    playerId: string,
    clientId: string
  ) {
    try {
      if (fcmToken) {
        const parameters = {
          userType: 'player',
          searchFunctionality: 'updatePlayerToken',
          fcmToken,
          playerId,
          clientId,
        };
        await api.post(`/search`, parameters);
      }
    } catch (error) {
      console.log('updatePlayerToken error: ', error);
    }
  }
  if (loadingAuth) {
    return <Loading />;
  }
  return (
    <authContext.Provider
      value={{
        signed: Boolean(playerAuth),
        playerAuth,
        client,
        player,
        loadingAuth,
        notifications,
        loadingClient,
        notificationCategory1,
        notificationCategory2,
        notificationCount,
        isEmptyCorretor,
        isBrokerDomain,
        setClient,
        getNotifications,
        getClientByAliasAndPlayer,
        getClientByUrl,
        getClientByClientIdAndPlayerId,
        signIn,
        setNotificationCount,
        setIsEmptyCorretor,
        setPlayerAuth,
        setPlayer,
        signOut,
      }}
    >
      {client && (
        <Helmet>
          <style type="text/css">
            {`
            :root {
                --primary: ${client.primaryColor};
                --secondary: ${client.primaryContrastColor};
            }
            `}
          </style>
          <link rel="icon" href={resolveFavicon(client.clientFavicon)} />
        </Helmet>
      )}
      {props.children}
    </authContext.Provider>
  );
}
